import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Package, ChevronRight } from "lucide-react";

const STATUS_LABELS = {
  pending: "Awaiting vendor", confirmed: "Confirmed", preparing: "Cooking",
  out_for_delivery: "Out for delivery", delivered: "Delivered", cancelled: "Cancelled",
};
const STATUS_COLOR = { pending: "white", confirmed: "lime", preparing: "lime",
  out_for_delivery: "pink", delivered: "lime", cancelled: "pink" };

export default function MyOrders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const reload = () => api.get("/food/orders").then(r => setOrders(r.data)).catch(()=>{});
  useEffect(() => { reload(); }, []);

  if (!user) return <div className="max-w-3xl mx-auto px-5 py-20 text-center">
    <h1 className="font-display font-black text-3xl">Sign in to see your orders</h1>
    <Link to="/login" className="brut-btn mt-4 inline-flex">Sign in</Link>
  </div>;

  const isVendor = user.role === "vendor";
  const isDelivery = user.role === "delivery_agent";

  const next = async (oid, status) => {
    try { await api.patch(`/food/orders/${oid}/status`, { status }); toast.success(`Order ${status}`); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="paper min-h-[80vh]" data-testid="orders-page">
      <div className="max-w-4xl mx-auto px-5 md:px-10 py-10">
        <h1 className="font-display font-black text-4xl">{isVendor ? "Food orders" : isDelivery ? "Deliveries" : "Your orders"}</h1>
        <p className="text-[#5A5A5A] mt-1">{orders.length} order{orders.length !== 1 ? "s" : ""}</p>

        <div className="space-y-3 mt-6">
          {orders.length === 0 && (
            <div className="brut-card p-12 text-center">
              <Package size={32} className="mx-auto"/>
              <div className="font-display font-black text-2xl mt-2">No orders yet</div>
              <p className="text-[#5A5A5A] mt-1">{isVendor ? "Orders will appear here once students start placing them." : "Browse vendors and start your first order."}</p>
              {!isVendor && <Link to="/vendors" className="brut-btn mt-4 inline-flex">Browse vendors</Link>}
            </div>
          )}
          {orders.map(o => (
            <div key={o.id} className="brut-card p-5" data-testid={`order-${o.id}`}>
              <div className="flex flex-wrap justify-between items-start gap-2">
                <div>
                  <div className="font-bold">{o.vendor_name}</div>
                  <div className="text-xs text-[#5A5A5A]">#{o.id.slice(0, 8)} · {o.created_at?.slice(0, 16).replace("T", " ")}</div>
                </div>
                <span className={`brut-tag ${STATUS_COLOR[o.status] || "white"}`}>{STATUS_LABELS[o.status] || o.status}</span>
              </div>
              <div className="mt-3 text-sm">
                {o.items.map((it, i) => <div key={i}>{it.qty} × {it.name} <span className="text-[#5A5A5A]">— ₹{it.price * it.qty}</span></div>)}
              </div>
              <div className="mt-3 flex justify-between items-end">
                <div className="text-xs text-[#5A5A5A]">
                  <div>📍 {o.delivery_address}</div>
                  <div>💳 {o.payment_method.toUpperCase()}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-[#5A5A5A]">Total</div>
                  <div className="font-display font-black text-2xl">₹{o.total}</div>
                </div>
              </div>

              {/* Vendor / delivery actions */}
              {isVendor && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {o.status === "pending" && <button className="brut-btn !text-xs" onClick={()=>next(o.id, "confirmed")}>Accept</button>}
                  {o.status === "confirmed" && <button className="brut-btn !text-xs" onClick={()=>next(o.id, "preparing")}>Start cooking</button>}
                  {o.status === "preparing" && <button className="brut-btn !text-xs" onClick={()=>next(o.id, "out_for_delivery")}>Out for delivery</button>}
                  {o.status === "out_for_delivery" && <button className="brut-btn !text-xs" onClick={()=>next(o.id, "delivered")}>Mark delivered</button>}
                  {!["delivered", "cancelled"].includes(o.status) && <button className="brut-btn pink !text-xs" onClick={()=>next(o.id, "cancelled")}>Cancel</button>}
                </div>
              )}
              {isDelivery && o.status === "out_for_delivery" && (
                <button className="brut-btn !text-xs mt-3" onClick={()=>next(o.id, "delivered")}>Mark delivered</button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
